import { useMemo, useRef } from "react";
import { useTheme, css } from "@emotion/react";
import { Interpolation, Theme } from "@emotion/react";
import { useRouter } from "next/router";
import { ExtensionLink } from "components/core/ExtensionLink";
import { Button } from "components/core/buttons/Button";
import { ContentCard } from "components/domain/contents/cards/ContentCard";
import { SectionScaffold } from "components/domain/contents/sections/SectionScaffold";
import { useMixContentQuery } from "graphql/generated";
import { fireEvent } from "lib/ga";
import { slidein } from "styles/animation/slidein";
import { LIMIT } from "utils/buildQueryVariables";
import { ContentSection } from "types/app";

export const OriginalSection = ({
  contentSection,
  variables,
  limit = LIMIT.original,
  ...props
}: {
  contentSection: ContentSection;
  variables: MixContentQueryVariables;
  limit?: number;
} & { css?: Interpolation<Theme> }) => {
  const router = useRouter();
  const ref = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const { data } = useMixContentQuery({
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    variables: {
      ...variables,
      limit,
      offset: 0,
    },
  });

  const contents = useMemo(() => (data ? data.mix_content : []), [data]);

  return (
    <SectionScaffold {...props}>
      <SectionScaffold.Header>
        <SectionScaffold.Title
          css={css`
            color: ${(theme as any).mode === "dark"
              ? theme.palette.common.white.main
              : theme.palette.text.primary};
          `}
        >
          오리지널 콘텐츠
        </SectionScaffold.Title>
        <SectionScaffold.Controls>
          <ExtensionLink href="/original">
            <Button
              size="small"
              color="grey"
              variant="text"
              endIcon="ChevronRight"
            >
              더보기
            </Button>
          </ExtensionLink>
        </SectionScaffold.Controls>
      </SectionScaffold.Header>
      <SectionScaffold.Contents>
        <div
          ref={ref}
          css={css`
            display: grid;
            grid-auto-flow: column;
            grid-column-gap: 24px;
            overflow: auto;
            scroll-snap-type: x mandatory;
            scroll-padding: var(--page-padding);
            animation: 600ms ${slidein};

            ::-webkit-scrollbar {
              display: none;
            }

            @media (max-width: 1024px) {
              display: flex;
              gap: 24px;
              max-width: 100vw;
              padding: 0 var(--page-padding);
              margin: 0 calc(-1 * var(--page-padding));
            }

            @media (max-width: 768px) {
              gap: 12px;
            }
          `}
        >
          {contents.map((content) => (
            <ContentCard
              css={css`
                scroll-snap-align: start;
                width: 320px;
              `}
              key={content.id}
              type={content.released ? "original" : "upcoming"}
              device="web+tablet"
              content={content}
              onClick={() => {
                const params = {
                  content_section: contentSection,
                  content_url: content.originalurl,
                  content_isoriginal: true,
                  content_released: content.released,
                };

                fireEvent({
                  event: "content_click",
                  params: {
                    content_id: content.id,
                    content_title: content.title,
                    ...params,
                  },
                });

                // window.fbq("track", "ViewContent", {
                //   ...params,
                //   content_ids: [content.id],
                //   content_name: content.title,
                //   content_type: "product",
                // });
              }}
            />
          ))}
        </div>
      </SectionScaffold.Contents>
    </SectionScaffold>
  );
};
